import React from 'react';
import { Box, alpha, keyframes } from '@mui/material';
import centrajobLogo from '@/assets/images/CentraJobLogo_New.svg';

// Animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const progressBar = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Theme colors
const colors = {
  primary: '#2563EB',
  primaryLight: '#3B82F6',
  secondary: '#7C3AED',
  gray200: '#E5E7EB',
  gray400: '#9CA3AF',
};

interface SplashScreenProps {
  message?: string;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ message }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(145deg, ${alpha(colors.primary, 0.08)} 0%, ${alpha('#E0E7FF', 0.5)} 25%, ${alpha(colors.secondary, 0.06)} 50%, ${alpha('#DDD6FE', 0.4)} 75%, ${alpha(colors.primary, 0.08)} 100%)`,
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: -100, md: -150 },
          right: { xs: -100, md: -80 },
          width: { xs: 300, md: 450 },
          height: { xs: 300, md: 450 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(colors.primary, 0.15)} 0%, ${alpha(colors.primary, 0.05)} 50%, transparent 70%)`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: -80, md: -100 },
          left: { xs: -80, md: -60 },
          width: { xs: 280, md: 400 },
          height: { xs: 280, md: 400 },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(colors.secondary, 0.12)} 0%, ${alpha(colors.secondary, 0.04)} 50%, transparent 70%)`,
        }}
      />

      {/* Main content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Logo with float animation */}
        <Box
          sx={{
            animation: `${float} 3s ease-in-out infinite`,
          }}
        >
          <Box
            component="img"
            src={centrajobLogo}
            alt="CentraJob"
            sx={{
              height: { xs: 40, sm: 48, md: 52 },
              width: 'auto',
            }}
          />
        </Box>

        {/* Progress bar */}
        <Box
          sx={{
            width: { xs: 120, md: 160 },
            height: 4,
            borderRadius: '4px',
            background: alpha(colors.primary, 0.15),
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              borderRadius: '4px',
              background: `linear-gradient(90deg, ${colors.primary}, ${colors.primaryLight})`,
              backgroundSize: '200% 100%',
              animation: `${progressBar} 2s ease-in-out infinite, ${shimmer} 1.5s linear infinite`,
            }}
          />
        </Box>

        {/* Loading text */}
        {message && (
          <Box
            sx={{
              fontSize: { xs: '0.8rem', md: '0.85rem' },
              fontWeight: 500,
              color: colors.gray400,
              letterSpacing: '0.3px',
            }}
          >
            {message}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 24, md: 32 },
          fontSize: '0.75rem',
          color: colors.gray400,
          fontWeight: 500,
        }}
      >
        &copy; {new Date().getFullYear()} CentraJob
      </Box>
    </Box>
  );
};

export default SplashScreen;
